import React, { useEffect, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { Modal, Field, inputCls, PrimaryBtn, GhostBtn, DangerBtn, DataTable } from "../components/DataTable";
import SiteSelector, { useSiteSelector } from "../components/SiteSelector";
import { toast } from "sonner";

const empty = { site_id: "", name: "", device_type: "PLC", protocol: "Modbus", address: "", status: "online", external_id: "", connector: "none", host: "", port: 502, unit_id: 1, opcua_url: "", poll_interval_ms: 1000 };

export default function Devices() {
  const { sites, siteId, setSiteId } = useSiteSelector();
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editId, setEditId] = useState(null);

  const load = () => siteId && api.get("/devices", { params: { site_id: siteId } }).then(({ data }) => setRows(data));
  useEffect(() => { load(); }, [siteId]);

  const save = async (e) => {
    e.preventDefault();
    try {
      if (editId) await api.put(`/devices/${editId}`, form);
      else await api.post("/devices", form);
      toast.success("Saved");
      setOpen(false); setForm(empty); setEditId(null); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const remove = async (id) => {
    if (!confirm("Delete device and its tags?")) return;
    await api.delete(`/devices/${id}`); load(); toast.success("Deleted");
  };

  const siteName = (id) => sites.find(s => s.id === id)?.name || "—";

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
        <div>
          <div className="scada-label">FIELD HARDWARE</div>
          <h1 className="font-heading font-black text-4xl tracking-tighter">Devices</h1>
        </div>
        <div className="flex items-center gap-3">
          <SiteSelector sites={sites} siteId={siteId} setSiteId={setSiteId} />
          <PrimaryBtn data-testid="add-device-btn" onClick={() => { setForm({ ...empty, site_id: siteId || sites[0]?.id || "" }); setEditId(null); setOpen(true); }}>+ Add Device</PrimaryBtn>
        </div>
      </div>

      <DataTable
        testId="devices-table"
        columns={[
          { key: "name", label: "NAME" },
          { key: "site_id", label: "SITE", render: r => siteName(r.site_id) },
          { key: "device_type", label: "TYPE" },
          { key: "protocol", label: "PROTOCOL" },
          { key: "address", label: "ADDRESS" },
          { key: "external_id", label: "DEV.ID (GW)", render: r => <span className="font-mono-metric text-[#00E5FF]">{r.external_id || "—"}</span> },
          { key: "status", label: "STATUS", render: r => (
            <span className="font-mono-metric uppercase text-[10px]" style={{ color: r.status === "online" ? "#00E676" : r.status === "warning" ? "#FFB300" : "#78909C" }}>{r.status}</span>
          ) },
        ]}
        rows={rows}
        actions={(r) => (
          <>
            <GhostBtn data-testid={`edit-device-${r.id}`} onClick={() => { setForm(r); setEditId(r.id); setOpen(true); }}>Edit</GhostBtn>
            <DangerBtn data-testid={`del-device-${r.id}`} onClick={() => remove(r.id)}>Del</DangerBtn>
          </>
        )}
      />

      <Modal open={open} onClose={() => setOpen(false)} title={editId ? "Edit Device" : "Add Device"} testId="device-modal">
        <form onSubmit={save}>
          <Field label="Site">
            <select required value={form.site_id} onChange={(e) => setForm({ ...form, site_id: e.target.value })} className={inputCls()} data-testid="device-site">
              <option value="">— select —</option>
              {sites.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
          </Field>
          <Field label="Name"><input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={inputCls()} data-testid="device-name" /></Field>
          <Field label="Type">
            <select value={form.device_type} onChange={(e) => setForm({ ...form, device_type: e.target.value })} className={inputCls()}>
              <option>PLC</option><option>Gateway</option><option>RTU</option><option>HMI</option>
            </select>
          </Field>
          <Field label="Protocol">
            <select value={form.protocol} onChange={(e) => setForm({ ...form, protocol: e.target.value })} className={inputCls()}>
              <option>Modbus</option><option>MQTT</option><option>OPC-UA</option><option>HTTP</option>
            </select>
          </Field>
          <Field label="Address"><input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} className={inputCls()} /></Field>
          <Field label="Gateway DeviceID (external)"><input value={form.external_id || ""} onChange={(e) => setForm({ ...form, external_id: e.target.value })} placeholder="e.g. 1" className={inputCls()} data-testid="device-external-id" /></Field>
          <Field label="Server-side Connector">
            <select value={form.connector} onChange={(e) => setForm({ ...form, connector: e.target.value })} className={inputCls()} data-testid="device-connector">
              <option value="none">none (push from gateway)</option>
              <option value="modbus_tcp">Modbus TCP (poll)</option>
              <option value="opcua">OPC-UA (poll)</option>
            </select>
          </Field>
          {form.connector === "modbus_tcp" && (
            <div className="grid grid-cols-3 gap-3">
              <Field label="Host"><input value={form.host || ""} onChange={(e) => setForm({ ...form, host: e.target.value })} placeholder="192.168.1.10" className={inputCls()} /></Field>
              <Field label="Port"><input type="number" value={form.port || 502} onChange={(e) => setForm({ ...form, port: parseInt(e.target.value) || 502 })} className={inputCls()} /></Field>
              <Field label="Unit ID"><input type="number" value={form.unit_id || 1} onChange={(e) => setForm({ ...form, unit_id: parseInt(e.target.value) || 1 })} className={inputCls()} /></Field>
            </div>
          )}
          {form.connector === "opcua" && (
            <Field label="OPC-UA Endpoint URL"><input value={form.opcua_url || ""} onChange={(e) => setForm({ ...form, opcua_url: e.target.value })} placeholder="opc.tcp://server:4840" className={inputCls()} /></Field>
          )}
          {form.connector !== "none" && (
            <Field label="Poll Interval (ms)"><input type="number" value={form.poll_interval_ms || 1000} onChange={(e) => setForm({ ...form, poll_interval_ms: parseInt(e.target.value) || 1000 })} className={inputCls()} /></Field>
          )}
          <Field label="Status">
            <select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} className={inputCls()}>
              <option value="online">online</option><option value="warning">warning</option><option value="offline">offline</option>
            </select>
          </Field>
          <div className="flex justify-end gap-2 mt-4"><PrimaryBtn type="submit" data-testid="save-device">Save</PrimaryBtn></div>
        </form>
      </Modal>
    </div>
  );
}
